import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Row,
  Col,
  Spin,
  Input,
  Button,
  Checkbox,
  message,
  Tooltip,
} from "../../common/utils/antd-imports/AntdImports";
import { useDispatch, useSelector } from "react-redux";
import {
  exportToExcel,
  getSelectedColumnsToExport,
} from "../../../store/thunks/buyer-thunks/BuyerThunks";
import "./ExportExcel.css";
import { ReactComponent as DeleteIcon } from "../../../assests/trash-delete-icon.svg";
import { ReactComponent as DragDropIcon } from "../../../assests/drag-drop-icon.svg";
import "../../../main.css";
import { useMediaQuery } from "react-responsive";
import { ReactComponent as BackArrow } from "../../../assests/back-arrow.svg";
import { useCallback } from "react";
import { debounce } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import CampaignDetails from "../../common/campaigns/campaigndetails/CampaignDetails";

const ExportExcel = ({
  exportColumnsModal,
  setExportColumnsModal,
  campaignId,
  campaignProducts,
}) => {
  const { exportColumnsLoading, availableColumns, selectedColumns } =
    useSelector((state) => state.buyerCampaigns);
  const isMobile = useMediaQuery({ maxWidth: 768});
  const products = sessionStorage.getItem('products') ? JSON.parse(sessionStorage.getItem('products')) :"";
  const cid = sessionStorage.getItem('campaignCid');
  const [selectedCols, setSelectedCols] = useState([]);
  const [availableCols, setAvailableCols] = useState([]);
  const [initialAvailableCols, setInitialAvailableCols] = useState([]);
  const [totalCols, setTotalCols] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const CheckboxGroup = Checkbox.Group;
  const [checkedList, setCheckedList] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef(null);

  // Use refs for mobile drag state to avoid re-renders
  const touchStateRef = useRef({
    dragIndex: null,
    hoverIndex: null,
    isDragging: false,
    startX: 0,
    startY: 0,
  });

  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();
  const checkAll = availableCols?.length === checkedList?.length;
  const indeterminate =
    checkedList?.length > 0 && checkedList?.length < availableColumns.length;

  // Updated mobile touch handlers with delete detection
  const handleMobileTouchStart = (index, e) => {
    // Check if the touch target is the delete icon or its parent
    const isDeleteAction =
      e.target.closest(".delete-icon") ||
      e.target.classList.contains("delete-icon") ||
      e.target.closest(".align-right");

    if (isDeleteAction) {
      e.preventDefault();
      e.stopPropagation();
      return; // Exit early if it's a delete action
    }

    // Only proceed with drag if it's not a delete action
    const touch = e.touches[0];
    touchStateRef.current = {
      dragIndex: index,
      hoverIndex: null,
      isDragging: false,
      startX: touch.clientX,
      startY: touch.clientY,
    };

    e.preventDefault();
  };

  const handleMobileTouchMove = (e) => {
    const touchState = touchStateRef.current;

    if (touchState.dragIndex === null) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchState.startX);
    const deltaY = Math.abs(touch.clientY - touchState.startY);

    // Only start dragging after a minimum movement to distinguish from click
    if (!touchState.isDragging && (deltaX > 10 || deltaY > 10)) {
      touchState.isDragging = true;
    }

    if (touchState.isDragging) {
      e.preventDefault();

      const targetElement = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );
      if (!targetElement) return;

      const liElement = targetElement.closest("li.col-label");
      if (liElement) {
        // Find the index by looking at the parent's children
        const parent = liElement.parentNode;
        const children = Array.from(parent.children);
        const hoverIndex = children.indexOf(liElement);

        if (hoverIndex !== -1 && hoverIndex !== touchState.hoverIndex) {
          touchState.hoverIndex = hoverIndex;
        }
      }
    }
  };

  const handleMobileTouchEnd = (e) => {
    const touchState = touchStateRef.current;

    // If it was a drag operation, handle the reorder
    if (
      touchState.isDragging &&
      touchState.dragIndex !== null &&
      touchState.hoverIndex !== null
    ) {
      const newItems = [...selectedCols];
      const draggedItem = newItems[touchState.dragIndex];
      newItems.splice(touchState.dragIndex, 1);
      newItems.splice(touchState.hoverIndex, 0, draggedItem);
      setSelectedCols(newItems);
    }

    // Reset touch state
    touchStateRef.current = {
      dragIndex: null,
      hoverIndex: null,
      isDragging: false,
      startX: 0,
      startY: 0,
    };

    e.preventDefault();
  };

  // Enhanced delete handler with better event prevention
  const handleDeleteColumn = (column, e) => {
    e.stopPropagation();
    e.preventDefault();

    // For touch devices, ensure we're not in a drag operation
    if (touchStateRef.current.isDragging) {
      return;
    }

    deselectColumn(column);
  };

  // Desktop events for Drag and Drop
  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (index) => {
    const newItems = [...selectedCols];
    const draggedItem = newItems[dragIndex];
    newItems.splice(dragIndex, 1);
    newItems.splice(index, 0, draggedItem);
    setSelectedCols(newItems);
    setDragIndex(null);
  };

  // Delete select columns and moving it to available column
  const deselectColumn = (column) => {
    setSearchValue("");
    const updatedSelectedCols = selectedCols.filter(
      (col) => col.field_name != column.field_name
    );
    const updatedAvailableCols = [column, ...availableCols];
    const updatedCheckedList = checkedList.filter(
      (field) => field != column.field_name
    );
    setSelectedCols(updatedSelectedCols);
    setAvailableCols(updatedAvailableCols);
    setInitialAvailableCols(updatedAvailableCols);
    setCheckedList(updatedCheckedList);
  };

  // Rest of your existing functions remain the same...
  const onChange = (list) => {
    const selectedFieldName = list[0];
    const selectedCol = availableCols.find(
      (col) => col.field_name === selectedFieldName
    );
    if (!selectedCol) return;

    const updatedAvailableCols = availableCols.filter(
      (col) => col.field_name !== selectedFieldName
    );
    setAvailableCols(updatedAvailableCols);
    setSelectedCols([selectedCol, ...selectedCols]);
  };

  const onCheckAllChange = (e) => {
    if (e.target.checked) {
      const updatedSelectedCols = [...selectedCols];
      availableCols.forEach((col) => {
        if (
          !updatedSelectedCols.some(
            (selected) => selected.field_name === col.field_name
          )
        ) {
          updatedSelectedCols.push(col);
        }
      });
      setSelectedCols(updatedSelectedCols);
      setCheckedList(availableCols.map((col) => col.field_name));
      setAvailableCols([]);
    } else {
      setAvailableCols([...availableCols, ...selectedCols]);
      setSelectedCols([]);
      setCheckedList([]);
    }
  };

  const handleExportExcel = () => {
    setIsLoading(true);
    if (!isMobile) setExportColumnsModal(false);
    let payload = {
      report_type: "report_campaign",
      selected_columns_post: JSON.stringify(selectedCols),
    };
    dispatch(exportToExcel(payload)).then((res) => {
      setIsLoading(false);
      if (res.payload.status == 1) {
        const headers = {};
        selectedCols.forEach((item) => {
          if (item.label === "DMA") {
            headers[`${item.field_name}_dma`] = item.label;
          } else if (item.label === "Break/Loop Length") {
            headers[`break_${item.field_name}`] = item.label;
          } else {
            headers[item.field_name] = item.label;
          }
        });

        const csvData = convertToCSV(
          headers,
          isMobile ? products : campaignProducts
        );
        const formattedDate = new Date().toISOString().slice(0, 10);
        const fileName = isMobile
          ? `Campaign_Products_${cid}_${formattedDate}`
          : `Campaign_Products_${campaignId}_${formattedDate}`;
        downloadCSVFile(fileName, csvData);
        message.success("CSV file is downloading");
      } else {
        message.error("Error while exporting CSV");
      }
      sessionStorage.removeItem("campaignCid");
      sessionStorage.removeItem("campaignProducts");
    });
  };

  const convertToCSV = (headers, products) => {
    let csv = Object.values(headers).join(",") + "\r\n";

    products?.forEach((row) => {
      const rowData = Object.keys(headers).map((col) => {
        let fieldData = "";

        if (col.includes("::")) {
          const [f1, f2] = col.split("::");
          fieldData = row[f1] || row[f2];
        } else if (["booked_from", "booked_to"].includes(col)) {
          fieldData = formatDate(row[col], row["area_time_zone_type"]);
        } else if (["from_date", "to_date"].includes(col)) {
          fieldData = formatDateFromNumber(
            row[col],
            row["area_time_zone_type"]
          );
        } else if (["price", "offerprice", "cpm"].includes(col)) {
          fieldData = `$${Number(row[col]).toFixed(2)}`;
        } else if (col === "impressionsperselectedDates") {
          fieldData = Number(row[col]).toFixed(0);
        } else if (col === "sold_status") {
          fieldData = row[col] ? "Sold out" : "Available";
        } else if (col === "total") {
          fieldData = `$${(row["quantity"] * row["price"]).toFixed(2)}`;
        } else if (col === "state_name_dma") {
          fieldData = row["state_name"];
        } else if (col === "ageloopLength") {
          fieldData = ["Digital", "Digital/Static"].includes(row.type)
            ? row["ageloopLength"]
            : "";
        } else if (col === "break_ageloopLength") {
          fieldData = row.type === "Media" ? row["ageloopLength"] : "";
        } else {
          fieldData = row[col];
        }

        return (fieldData || "")
          .toString()
          .replace(/,/g, "")
          .replace(/:/g, "")
          .replace(/;/g, "")
          .replace(/\r\n/g, "");
      });

      csv += rowData.join(",") + "\r\n";
    });

    return csv;
  };

  const downloadCSVFile = (fileName, csvData) => {
    const blob = new Blob(["\ufeff" + csvData], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dt, timezone) => {
    return new Date(dt).toLocaleDateString();
  };

  const formatDateFromNumber = (dt, timezone) => {
    const dtValue = typeof dt === "object" ? dt?.$date?.$numberLong : dt;
    return new Date(+dtValue).toLocaleDateString();
  };

  const handleExportColumnSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue.trim() == "") {
        setAvailableCols(initialAvailableCols);
        return;
      }
      if (searchValue) {
        const searchedCols = initialAvailableCols.filter((column) =>
          column.label.toLowerCase().includes(searchValue)
        );
        setAvailableCols(searchedCols);
        return;
      }
    }, 300)
  );
// Helper method for disabling export button if there are no products
  const hasNoproducts = isMobile ? !products || products.length == 0 : !campaignProducts || campaignProducts.length == 0;
  const modalFooter = (
    <>
      <Button
        className="export-footer-btn export-cancel-btn"
        style={{ marginRight: "0.5rem" }}
        onClick={() => {
          if (!isMobile) setExportColumnsModal(false);
          else navigate(-1);
        }}
      >
        Cancel
      </Button>
      <div ref={tooltipRef}>
        <Tooltip
          trigger={isMobile && hasNoproducts ? "click" : "hover"}
          onOpenChange={(visible) => {
            if (hasNoproducts) setVisible(visible);
          }}
          open={hasNoproducts ? visible : undefined}
          title={
            hasNoproducts
              ? "There are no products in the campaign.Please add products to export data"
              : null
          }
        >
          <Button
            disabled={hasNoproducts}
            className={`export-footer-btn ${
              hasNoproducts ? "restrict-button": "export-btn"
            }`}
            onClick={() =>
              handleExportExcel(
                selectedCols,
                isMobile ? products : campaignProducts
              )
            }
          >
            Export Excel
          </Button>
        </Tooltip>
      </div>
    </>
  );

  const exportExcelUISegment = (
    <>
      {!isMobile && (
        <span>
          Choose the columns you want to export. Move items from left to right
          and drag to arrange their order.
        </span>
      )}
      {exportColumnsLoading ? (
        <Spin className="export-spin"></Spin>
      ) : (
        <Row gutter={78} className="export-column-body">
          <Col span={!isMobile ? 12 : 24}>
            <div className="col-header">
              Available Columns({availableCols?.length})
            </div>
            <div className={`${isMobile ? "reverse-content" : ""}`}>
              <div className="export-container available-cols">
                <div className="search-section">
                  <Input
                    placeholder={"Search"}
                    className="search-column"
                    value={searchValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchValue(value);
                      handleExportColumnSearch(value);
                    }}
                  />
                </div>
                <Checkbox
                  indeterminate={indeterminate}
                  onChange={onCheckAllChange}
                  checked={checkAll}
                  disabled={availableCols?.length == 0}
                >
                  Check all
                </Checkbox>

                <div className="available-column">
                  <CheckboxGroup
                    options={availableCols?.map((col) => ({
                      label: col.label,
                      value: col.field_name,
                    }))}
                    value={checkedList}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div className="export-help-text">
                Choose the columns you want to export
              </div>
            </div>
          </Col>

          <Col span={!isMobile ? 12 : 24}>
            <div className="col-header selected-mobile">
              Selected Columns({selectedCols?.length})
            </div>
            <div className={`${isMobile ? "reverse-content" : ""}`}>
              <div className="export-container selected-cols">
                {selectedCols?.map((column, index) => {
                  return (
                    <li
                      className="col-label"
                      key={index}
                      draggable
                      onTouchStart={
                        isMobile
                          ? (e) => handleMobileTouchStart(index, e)
                          : undefined
                      }
                      onTouchMove={
                        isMobile ? (e) => handleMobileTouchMove(e) : undefined
                      }
                      onTouchEnd={
                        isMobile ? (e) => handleMobileTouchEnd(e) : undefined
                      }
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(index)}
                    >
                      <DragDropIcon className="pointer set-margin" />
                      {column.label}
                      <span className="align-right">
                        <DeleteIcon
                          className="delete-icon"
                          onClick={(e) => handleDeleteColumn(column, e)}
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                          style={{ cursor: "pointer" }}
                        />
                      </span>
                    </li>
                  );
                })}
              </div>
              <div className="export-help-text">
                Drag to rearrange the column order
              </div>
            </div>
          </Col>
        </Row>
      )}

      {!isMobile && (
        <Row justify="end" className="footer-buttons">
          {modalFooter}
        </Row>
      )}
      {isMobile && <Row className="mobile-footer">{modalFooter}</Row>}
    </>
  );

  useEffect(() => {
    if (!isMobile) return;

    const handleOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setVisible(false);
      }
    };

    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("mousedown", handleOutside);

    return () => {
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [isMobile]);

  useEffect(() => {
    setSelectedCols(selectedColumns);
    setAvailableCols(availableColumns);
    setInitialAvailableCols(availableColumns);
    setCheckedList([]);
    if (selectedColumns && availableColumns)
      setTotalCols([...selectedColumns, ...availableColumns]);
  }, [selectedColumns, availableColumns]);

  useEffect(() => {
    if (exportColumnsModal || isMobile) {
      dispatch(getSelectedColumnsToExport());
    }
  }, [dispatch, exportColumnsModal, isMobile]);

  return (
    <>
      {!isMobile ? (
        <Modal
          className="selected-columns-export-modal"
          open={exportColumnsModal}
          title={
            <span className="export-modal-title">Export Product Data</span>
          }
          footer={false}
          closable={true}
          onCancel={() => setExportColumnsModal(false)}
        >
          {exportExcelUISegment}
        </Modal>
      ) : (
        <div className="export-selection-container">
          <div className="export-header">
            <BackArrow className="mobile-arrow" onClick={() => navigate(-1)} />
            <span className="export-title">Export Product data</span>
          </div>
          {exportExcelUISegment}
        </div>
      )}
      {exportColumnsLoading ||
        (isLoading && (
          <div className="loading-css">
            <Spin size="large" fullscreen />
          </div>
        ))}
    </>
  );
};

export default ExportExcel;
